import { supabase } from './supabaseClient.js';
import { getExternalAuthHandlers } from '@/context/AuthContext.jsx';

const normalizeHeaders = (input) => {
  if (!input) {
    return {};
  }

  if (input instanceof Headers) {
    return Object.fromEntries(input.entries());
  }

  return { ...input };
};

const fetchSession = async (forceRefresh = false) => {
  const response = forceRefresh ? await supabase.auth.refreshSession() : await supabase.auth.getSession();
  if (response.error) {
    throw new Error(response.error.message || 'Impossibile recuperare la sessione Supabase.');
  }

  return response.data?.session || response.data || null;
};

const validateAdminSession = (session) => {
  if (!session?.access_token) {
    throw new Error('Sessione non trovata o scaduta. Effettua di nuovo l\'accesso come admin.');
  }

  const role = session.user?.app_metadata?.role;

  if (role !== 'admin') {
    throw new Error('Il tuo account non dispone dei permessi admin necessari per questa operazione.');
  }

  return session;
};

const getAdminSession = async ({ forceRefresh = false } = {}) => {
  const session = await fetchSession(forceRefresh);
  return validateAdminSession(session);
};

const triggerProviderSignOut = async () => {
  try {
    const handlers = getExternalAuthHandlers?.();
    const signOut = handlers?.signOut;
    if (typeof signOut === 'function') {
      await signOut();
    }
  } catch (error) {
    console.warn('[adminApiClient] Unable to trigger external signOut handler:', error);
  }
};

export const buildAdminRequestHeaders = async (headers, sessionOverride = null) => {
  const session = sessionOverride || (await getAdminSession());
  const normalizedHeaders = normalizeHeaders(headers);

  return {
    ...normalizedHeaders,
    Authorization: `Bearer ${session.access_token}`,
  };
};

export const adminFetch = async (input, init = {}) => {
  let session = await getAdminSession();
  let response = await fetch(input, {
    ...init,
    headers: await buildAdminRequestHeaders(init.headers, session),
  });

  if (response.status !== 401) {
    return response;
  }

  try {
    session = await getAdminSession({ forceRefresh: true });
  } catch (refreshError) {
    await triggerProviderSignOut();
    throw refreshError;
  }

  const retryResponse = await fetch(input, {
    ...init,
    headers: await buildAdminRequestHeaders(init.headers, session),
  });

  if (retryResponse.status === 401) {
    await triggerProviderSignOut();
  }

  return retryResponse;
};
